import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useEffectEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SCAEnrollmentState } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { MangopayScaIframeDialog } from '#src/components/fiatWallet/CreateFiatWallet/MangopayScaIframeDialog/index.tsx';
import { StepWrapper } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import { MangopayScaPopupCallback } from '#src/components/wallet/MangopayScaPopupCallback/index.tsx';
import { ORIGIN } from '#src/config.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useChildWindow } from '#src/hooks/useChildWindow.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { glossary } from '#src/lib/glossary.ts';

import { MangopayScaIntro } from '../MangopayScaIntro/index.tsx';
import type {
  GetCurrentUserScaStatusQuery,
  GetCurrentUserScaStatusQueryVariables,
  GetMangopayScaUrlQuery,
  GetMangopayScaUrlQueryVariables,
} from './__generated__/index.graphql.ts';

type Props = {
  onSuccess: () => void;
  setStep: (step: CreateFiatWalletSteps) => void;
};

const GET_SCA_URL_QUERY = gql`
  mutation GetMangopayScaUrlQuery($input: enrollSCAInput!) {
    enrollSCA(input: $input) {
      redirectUrl
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<GetMangopayScaUrlQuery, GetMangopayScaUrlQueryVariables>;

const GET_CURRENT_USER_SCA_STATUS_QUERY = gql`
  query GetCurrentUserScaStatusQuery {
    currentUser {
      slug
      mangopayUser {
        id
        scaEnrollmentState
      }
    }
  }
` as TypedDocumentNode<
  GetCurrentUserScaStatusQuery,
  GetCurrentUserScaStatusQueryVariables
>;

const usePollCurrentUserScaStatus = ({
  enabled,
  onSuccess,
  onFailed,
}: {
  enabled: boolean;
  onSuccess: () => void;
  onFailed?: () => void;
}) => {
  const { data } = useQuery(GET_CURRENT_USER_SCA_STATUS_QUERY, {
    pollInterval: enabled ? 1000 : 0,
    skip: !enabled,
  });

  const onFailedEvent = useEffectEvent(() => {
    onFailed?.();
  });

  useEffect(() => {
    if (!enabled) return () => {};
    const timeout = setTimeout(() => {
      onFailedEvent();
    }, 10000);

    return () => clearTimeout(timeout);
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [enabled]);

  if (
    enabled &&
    data?.currentUser?.mangopayUser?.scaEnrollmentState ===
      SCAEnrollmentState.SCA_ENROLLED
  ) {
    onSuccess();
  }

  return null;
};

export const MangopayScaStep = ({ setStep, onSuccess }: Props) => {
  const { showNotification } = useSnackNotificationContext();
  const { openPopup, close: closeWindow } = useChildWindow();
  const {
    flags: { useIframeForScaFlow },
  } = useFeatureFlags();

  const [getMangopayScaUrl] = useMutation(GET_SCA_URL_QUERY, {
    showErrorsWithSnackNotification: true,
  });
  const [polling, setPolling] = useState(false);
  const [scaIframeUrl, setScaIframeUrl] = useState<string | null>(null);

  const onClose = () => {
    closeWindow();
  };

  const onPollSuccess = () => {
    setPolling(false);
    closeWindow();
    onSuccess();
  };

  const onScaFlowSuccess = () => {
    setPolling(true);
  };

  const onStatusNotChanged = () => {
    showNotification('scaStatusNotChanged');
    setPolling(false);
    onClose();
  };

  usePollCurrentUserScaStatus({
    enabled: polling,
    onSuccess: onPollSuccess,
    onFailed: onStatusNotChanged,
  });

  const onClick = async () => {
    if (!useIframeForScaFlow) {
      const popup = openPopup(
        `${ORIGIN}/mangopay-sca.html`,
        'mangopayScaWindow'
      );
      const result = await getMangopayScaUrl({
        variables: {
          input: {
            returnUrl: `${ORIGIN}/mangopay-sca.html`,
          },
        },
      });
      if (result.data?.enrollSCA?.redirectUrl && popup) {
        popup.location.href = result.data.enrollSCA.redirectUrl;
      } else if (result.data?.enrollSCA?.redirectUrl) {
        window.open(result.data.enrollSCA.redirectUrl, '_self');
      }
    } else {
      const result = await getMangopayScaUrl({
        variables: {
          input: {
            returnUrl: `${ORIGIN}/mangopay-sca.html`,
          },
        },
      });
      if (result.data?.enrollSCA?.redirectUrl) {
        setScaIframeUrl(result.data.enrollSCA.redirectUrl);
      }
    }
  };

  return (
    <>
      {!useIframeForScaFlow && (
        <MangopayScaPopupCallback
          onSuccess={onScaFlowSuccess}
          onFailed={() => {
            showNotification('scaFailed');
            onClose();
          }}
        />
      )}
      {!!useIframeForScaFlow && !!scaIframeUrl && (
        <MangopayScaIframeDialog
          url={scaIframeUrl}
          onSuccess={onScaFlowSuccess}
          onFailed={() => {
            showNotification('scaFailed');
            setScaIframeUrl(null);
          }}
          onClose={() => setScaIframeUrl(null)}
        />
      )}
      <StepWrapper
        setStep={setStep}
        step={CreateFiatWalletSteps.MANGOPAY_SCA}
        submitButton={
          <Button onClick={onClick} size="medium">
            <FormattedMessage {...glossary.continue} />
          </Button>
        }
      >
        <MangopayScaIntro />
      </StepWrapper>
    </>
  );
};
